// SPDX-License-Identifier: Elastic-2.0
/**
 * onboardingMock — MSW handlers for the F-351 HA orchestration onboarding
 * endpoints (controller readiness + commissioning jobs).
 *
 * The Add-device launcher (and Settings controllers section) fetch
 * GET /onboarding/controllers; the pairing progress view polls
 * GET /onboarding/jobs/{id} and can cancel via DELETE /onboarding/jobs/{id}.
 * Without these handlers any test/dev session that opens those flows fails
 * with onUnhandledRequest: "error".
 *
 * URL patterns use regex on the host so they match both:
 *   - http://localhost:8200/api/v1/onboarding/* (vitest / unit tests)
 *   - /api/mirlai/v1/onboarding/*               (add-on single-host mode)
 */
import { HttpResponse, http } from "msw";

import type {
  CommissioningJob,
  ControllersStatus,
} from "@/api/onboardingClient";

const CONTROLLERS_RE = /\/onboarding\/controllers$/;
const JOB_RE = /\/onboarding\/jobs\/[^/]+$/;

/** Both Matter and Zigbee controllers ready to commission. */
export const FIXTURE_CONTROLLERS_ALL_READY: ControllersStatus = {
  ha_reachable: true,
  checked_at: "2026-06-01T12:00:00Z",
  controllers: [
    {
      kind: "matter",
      available: true,
      integration_loaded: true,
      integration_state: "loaded",
      radio_present: null,
      detail: "Matter controller ready",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
    {
      kind: "zigbee",
      available: true,
      integration_loaded: true,
      integration_state: "loaded",
      radio_present: true,
      detail: "Zigbee radio detected",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
  ],
};

/** Matter ready; Zigbee unavailable (no radio detected). */
export const FIXTURE_CONTROLLERS_ZIGBEE_UNAVAILABLE: ControllersStatus = {
  ha_reachable: true,
  checked_at: "2026-06-01T12:00:00Z",
  controllers: [
    {
      kind: "matter",
      available: true,
      integration_loaded: true,
      integration_state: "loaded",
      radio_present: null,
      detail: "Matter controller ready",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
    {
      kind: "zigbee",
      available: false,
      integration_loaded: false,
      integration_state: null,
      radio_present: false,
      detail: "No Zigbee radio detected",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
  ],
};

/** Matter unavailable (integration not loaded); Zigbee ready. Used by the
 *  A2.6 onboarding-step test to assert the step self-skips on mount. */
export const FIXTURE_CONTROLLERS_MATTER_UNAVAILABLE: ControllersStatus = {
  ha_reachable: true,
  checked_at: "2026-06-01T12:00:00Z",
  controllers: [
    {
      kind: "matter",
      available: false,
      integration_loaded: false,
      integration_state: null,
      radio_present: null,
      detail: "Matter integration not loaded",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
    {
      kind: "zigbee",
      available: true,
      integration_loaded: true,
      integration_state: "loaded",
      radio_present: true,
      detail: "Zigbee radio detected",
      last_checked_at: "2026-06-01T12:00:00Z",
    },
  ],
};

/** A commissioning job returned by GET/DELETE /onboarding/jobs/{id}. */
export const FIXTURE_COMMISSIONING_JOB: CommissioningJob = {
  job_id: "fixture-job-01",
  kind: "matter",
  state: "starting",
  progress: 0.0,
  code_kind: null,
  device_ref: null,
  error: null,
  events: ["Job started"],
  started_at: "2026-06-01T12:00:00Z",
  updated_at: "2026-06-01T12:00:00Z",
};

/**
 * MSW handler set for the F-351 onboarding endpoints.
 * Add to `setupServer(...)` in any test that opens the add-device or
 * controllers-status flows. GET /onboarding/controllers returns the
 * "all ready" fixture by default.
 */
export const onboardingHandlers = [
  http.get(CONTROLLERS_RE, () =>
    HttpResponse.json(FIXTURE_CONTROLLERS_ALL_READY),
  ),
  http.get(JOB_RE, () => HttpResponse.json(FIXTURE_COMMISSIONING_JOB)),
  http.delete(JOB_RE, () =>
    HttpResponse.json({
      ...FIXTURE_COMMISSIONING_JOB,
      state: "cancelled",
      events: [...FIXTURE_COMMISSIONING_JOB.events, "Job cancelled"],
    }),
  ),
];
